import React, { Suspense } from "react";
import {
  Route,
  Routes,
} from "react-router-dom";

// ============================================
// CORE COMPONENTS - Load immediately (used most frequently)
// ============================================
import AdminLogin from "./pages/admin/Login";
import AdminUsersList from "./pages/admin/Users";
import User from "./pages/admin/User";

// ============================================
// LAZY LOADED COMPONENTS - Load on demand (reduces initial bundle ~40%)
// ============================================

// Heavy components with large dependencies (@react-pdf/renderer, pdfjs, etc)
const DownloadIdCard = React.lazy(() => import("./pages/DownloadIdCard"));
const IdentifyFingerprint = React.lazy(() => import("./pages/admin/IdentifyFingerprint"));
const ScanIDCard = React.lazy(() => import("./pages/admin/ScanIDCard"));

// Hall management (has large JSON file)
const HallAssignment = React.lazy(() => import("./pages/admin/HallAssignment"));
const HallSchedule = React.lazy(() => import("./pages/admin/HallSchedule"));

// User registration flows
const NameRegistrationForm = React.lazy(() => import("./pages/NameRegistration"));
const SearchAndUpdateNameRegistration = React.lazy(() => import("./pages/SearchAndUpdateNameRegistration"));
const UpdateNameRegistration = React.lazy(() => import("./pages/admin/UpdateNameRegistration"));
const PhotoUploadForm = React.lazy(() => import("./pages/PhotoUpload"));
const PhotoUpdate = React.lazy(() => import("./pages/admin/PhotoUpdate"));
const Finger = React.lazy(() => import("./pages/Finger"));
const FingerPrintUpdate = React.lazy(() => import("./pages/admin/FingerPrintUpdate"));

// Admin pages
const AdminUsersListFilter = React.lazy(() => import("./pages/admin/FilterUsers"));
const RegisteredUsers = React.lazy(() => import("./pages/admin/RegisteredUsers"));
const YouthInWork = React.lazy(() => import("./pages/admin/YouthInWork"));
const WorkEnablingOutreach = React.lazy(() => import("./pages/admin/WorkEnablingOutreach"));
const AttritionList = React.lazy(() => import("./pages/admin/AttritionList"));
const AdminOutlet = React.lazy(() => import("./pages/admin/Outlet"));
const IDCardDisplay = React.lazy(() => import("./pages/User"));

// Food and meal tracking
const FoodPage = React.lazy(() => import("./pages/admin/Food"));
const Meal = React.lazy(() => import("./pages/Meal"));
const FoodPollVoting = React.lazy(() => import("./pages/FoodPollVoting"));

// MSP test scores (admin / super-admin)
const TestScoresPage = React.lazy(() => import("./pages/admin/TestScores"));

// Camp and accommodation
const AccommodationDashboard = React.lazy(() => import("./pages/admin/Accommodation"));
const CampStatusPage = React.lazy(() => import("./pages/admin/CampStatus"));
const TabletCollectionPage = React.lazy(() => import("./pages/admin/TabletCollection"));

// Staff management
const CreateStaff = React.lazy(() => import("./pages/admin/CreateStaff"));
const StaffList = React.lazy(() => import("./pages/admin/StaffList"));
const SelectStaffForCamp = React.lazy(() => import("./pages/admin/SelectStaffForCamp"));
const StaffForCampList = React.lazy(() => import("./pages/StaffForCampList"));
const StaffScheduleManagement = React.lazy(() => import("./pages/admin/StaffScheduleManagement"));
const StaffAttendance = React.lazy(() => import("./pages/admin/StaffAttendance"));
const StaffProfile = React.lazy(() => import("./pages/StaffProfile"));

// Medical and health
const SearchAndRecordAppointment = React.lazy(() => import("./pages/health/SearchAndRecordAppointment"));
const MedicalInventoryEntry = React.lazy(() => import("./pages/admin/MedicalInventoryEntry"));
const MedicalInventoryEdit = React.lazy(() => import("./pages/admin/MedicalInventoryEdit"));
const MedicalInventoryList = React.lazy(() => import("./pages/admin/MedicalInventoryList"));

// Infants
const EnrollInfant = React.lazy(() => import("./pages/admin/EnrollInfant"));
const InfantList = React.lazy(() => import("./pages/admin/InfantList"));
const UpdateInfant = React.lazy(() => import("./pages/admin/UpdateInfant"));

// Audit
const AuditTrailPage = React.lazy(() => import("./pages/admin/AuditTrail"));

// Issue reports (admin)
const IssueReports = React.lazy(() => import("./pages/admin/IssueReports"));
const IssueReportDetail = React.lazy(() => import("./pages/admin/IssueReportDetail"));

// Reporting
const ReportEvents = React.lazy(() => import("./pages/admin/ReportEvents"));
const EventReporting = React.lazy(() => import("./pages/admin/EventReporting"));
const ReportDashboard = React.lazy(() => import("./pages/admin/ReportDashboard"));
const ReportConfig = React.lazy(() => import("./pages/admin/ReportConfig"));

// Issue Report
const IssueReport = React.lazy(() => import("./pages/IssueReport"));
const QRCodeDisplay = React.lazy(() => import("./pages/QRCodeDisplay"));

// Cooperatives
const CoopManagement = React.lazy(() => import("./pages/admin/CoopManagement"));
const CoopFormBuilder = React.lazy(() => import("./pages/admin/CoopFormBuilder"));
const CoopFormFill = React.lazy(() => import("./pages/admin/CoopFormFill"));
const CoopProgress = React.lazy(() => import("./pages/admin/CoopProgress"));
const CoopProgressClusterDetail = React.lazy(() =>
  import("./pages/admin/CoopProgressClusterDetail")
);
const CoopProgressCoopDetail = React.lazy(() => import("./pages/admin/CoopProgressCoopDetail"));
const CoopProgressCompletedClusters = React.lazy(() =>
  import("./pages/admin/CoopProgressCompletedClusters")
);
const CoopProgressCompletedCooperatives = React.lazy(() =>
  import("./pages/admin/CoopProgressCompletedCooperatives")
);
const CoopEntryAuditLogs = React.lazy(() => import("./pages/admin/CoopEntryAuditLogs"));

// ============================================
// LOADING FALLBACK COMPONENT
// ============================================
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f5f5'
  }}>
    <div style={{
      textAlign: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #e0e0e0',
        borderTop: '4px solid #2196F3',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <p style={{ color: '#666', margin: 0 }}>Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Main pages - load immediately */}
        <Route path="/users" element={<AdminUsersList />} />
        <Route path="/" element={<RegisteredUsers />} />
        <Route path="/login" element={<AdminLogin />} />
        <Route path="/login/:user" element={<AdminLogin />} />
        <Route path="/admin/view-user/:id" element={<User />} />
        <Route path="/admin/user/:id" element={<User />} />
        <Route path="/user-by-id/*" element={<User />} />

        {/* User management - lazy loaded */}
        <Route path="/update-user" element={<SearchAndUpdateNameRegistration />} />
        <Route path="/create-user" element={<NameRegistrationForm />} />
        <Route path="/user/:id" element={<IDCardDisplay />} />
        <Route path="/registered" element={<RegisteredUsers />} />
        <Route path="/youth-in-work" element={<YouthInWork />} />
        <Route path="/work-enabling-outreach" element={<WorkEnablingOutreach />} />
        <Route path="/attrition-list" element={<AttritionList />} />
        <Route path="/filter" element={<AdminUsersListFilter />} />
        <Route path="/admin/users/image/:id" element={<PhotoUpdate />} />
        <Route path="/admin/users/:id" element={<UpdateNameRegistration />} />

        {/* Admin outlet with nested route */}
        <Route path="/admin" element={<AdminOutlet />}>
          <Route path="users/fingerprint/:id" element={<FingerPrintUpdate />} />
        </Route>

        {/* Fingerprint and ID */}
        <Route path="/finger" element={<Finger />} />
        <Route path="/update-finger-print" element={<Finger />} />
        <Route path="/update-image" element={<PhotoUploadForm />} />
        <Route path="/id-card" element={<DownloadIdCard />} />
        <Route path="/identify-fingerprint" element={<IdentifyFingerprint />} />
        <Route path="/scan-id-card" element={<ScanIDCard />} />

        {/* Food and meals */}
        <Route path="/food" element={<FoodPage />} />
        <Route path="/meal" element={<Meal />} />
        <Route path="/food-poll-voting" element={<FoodPollVoting />} />

        {/* MSP test scores - admin / super-admin */}
        <Route path="/test-scores" element={<TestScoresPage />} />

        {/* Camp and accommodation */}
        <Route path="/accommodation" element={<AccommodationDashboard />} />
        <Route path="/camp-status" element={<CampStatusPage />} />
        <Route path="/tablet-collection" element={<TabletCollectionPage />} />

        {/* Hall management */}
        <Route path="/hall-assignment" element={<HallAssignment />} />
        <Route path="/hall-schedule" element={<HallSchedule />} />

        {/* Staff */}
        <Route path="/create-staff" element={<CreateStaff />} />
        <Route path="/staff" element={<StaffList />} />
        <Route path="/select-staff-for-camp" element={<SelectStaffForCamp />} />
        <Route path="/staff-for-camp" element={<StaffForCampList />} />
        <Route path="/staff-schedule" element={<StaffScheduleManagement />} />
        <Route path="/staff-attendance" element={<StaffAttendance />} />
        <Route path="/staff-profile/:id" element={<StaffProfile />} />

        {/* Health */}
        <Route path="/health/user/:id" element={<SearchAndRecordAppointment />} />
        <Route path="/health" element={<SearchAndRecordAppointment />} />

        {/* Medical inventory */}
        <Route path="/medical-inventory" element={<MedicalInventoryList />} />
        <Route path="/medical-inventory/entry" element={<MedicalInventoryEntry />} />
        <Route path="/medical-inventory/:id" element={<MedicalInventoryEdit />} />

        {/* Infants */}
        <Route path="/enroll-infant" element={<EnrollInfant />} />
        <Route path="/infants" element={<InfantList />} />
        <Route path="/update-infant/:infantId" element={<UpdateInfant />} />

        {/* Audit */}
        <Route path="/audit-trail" element={<AuditTrailPage />} />

        {/* Issue Reports (admin) */}
        <Route path="/admin/issue-reports" element={<IssueReports />} />
        <Route path="/admin/issue-reports/:id" element={<IssueReportDetail />} />

        {/* Issue Report */}
        <Route path="/issue-report" element={<IssueReport />} />
        <Route path="/issue-report-qr" element={<QRCodeDisplay />} />

        {/* Reporting */}
        <Route path="/report-events" element={<ReportEvents />} />
        <Route path="/report-events/:eventId" element={<EventReporting />} />
        <Route path="/report-dashboard" element={<ReportDashboard />} />
        <Route path="/report-config" element={<ReportConfig />} />

        {/* Cooperatives */}
        <Route path="/coop-management" element={<CoopManagement />} />
        <Route path="/coop-form-builder" element={<CoopFormBuilder />} />
        <Route path="/coop-form" element={<CoopFormFill />} />
        <Route path="/coop-progress/completed-clusters" element={<CoopProgressCompletedClusters />} />
        <Route
          path="/coop-progress/completed-cooperatives"
          element={<CoopProgressCompletedCooperatives />}
        />
        <Route path="/coop-progress/cluster/:clusterKey" element={<CoopProgressClusterDetail />} />
        <Route path="/coop-progress/coop/:coopId" element={<CoopProgressCoopDetail />} />
        <Route path="/coop-progress" element={<CoopProgress />} />
        <Route path="/coop-form-audit-log" element={<CoopEntryAuditLogs />} />
      </Routes>
    </Suspense>
  );
}

export default App;
